import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Mail } from "lucide-react";
import type { FunctionReturnType } from "convex/server";
import { api } from "../../../../convex/_generated/api";
import { Dialog } from "../ui/Dialog";
import { Button } from "../ui/button";

type Preview = FunctionReturnType<typeof api.emailNotices.preview>;
export function EmailNoticeReview({ preview, busy, error, result, onApprove }: {
  preview: Preview; busy: boolean; error: string | null; result: string | null; onApprove: () => void;
}) {
  const existing = new Set(preview.states.map(row => row.recipientEmail));
  const remaining = preview.recipients.filter(person => !existing.has(person.email)).length;
  return <div className="stack">
    <p>Send a separate email to each teammate, from your connected clinic mailbox. The message itself stays in Yodl.</p>
    <dl><dt><strong>From</strong></dt><dd>{preview.sender}</dd></dl>
    <details open><summary><strong>To · {preview.recipients.length} teammates</strong></summary>
      <ul style={{ maxHeight: 180, overflow: "auto", paddingLeft: 20 }}>{preview.recipients.map(person => <li key={person.principalId}>{person.name} <span className="nav-hint">({person.email})</span></li>)}</ul>
    </details>
    {preview.skipped > 0 ? <p className="nav-hint">{preview.skipped} teammates excluded because their channel is muted or their email is unavailable.</p> : null}
    <div className="form-notice"><strong>{preview.subject}</strong><p style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{preview.body}</p></div>
    {preview.states.length > 0 ? <details><summary>Email status</summary><ul>{preview.states.map(row => <li key={row.recipientEmail}>{row.recipientEmail} — {row.state === "uncertain" ? "Needs delivery check; will not be resent automatically" : row.state === "drafting" ? "Preparing draft" : row.state}</li>)}</ul></details> : null}
    {!preview.ready ? <p className="form-notice">Email notices are not connected yet. Your message remains available in Yodl.</p> : null}
    {error ? <p role="alert" className="form-error">{error}</p> : null}
    {result ? <p role="status">{result}</p> : null}
    <div className="dialog__actions"><Button variant="primary" disabled={busy || !preview.ready || remaining === 0} onClick={onApprove}><Mail size={14} />{busy ? "Queueing…" : remaining === 0 ? "No new recipients" : `Send ${remaining} ${remaining === 1 ? "email" : "emails"}`}</Button></div>
  </div>;
}

export function EmailImportantNotice({ messageId }: { messageId: string }) {
  const configuration = useQuery(api.emailNotices.configuration, {});
  const [open, setOpen] = useState(false);
  const preview = useQuery(api.emailNotices.preview, open ? { messageId } : "skip");
  const approve = useMutation(api.emailNotices.approve);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  if (!configuration?.canSend) return null;
  const send = async () => {
    if (!preview?.allowed || busy) return;
    setBusy(true); setError(null);
    try {
      const response = await approve({ messageId, fingerprint: preview.fingerprint });
      setResult(response.queued > 0 ? `${response.queued} ${response.queued === 1 ? "email queued" : "emails queued"}. Delivery status will update here.` : "This notice was already requested for these teammates.");
    } catch { setError("The notice could not be queued. Review the current recipients and connection, then try again."); }
    finally { setBusy(false); }
  };
  return <>
    <button type="button" className="acknowledge-button" onClick={() => { setOpen(true); setError(null); setResult(null); }}><Mail size={14} /> Email important notice</button>
    {open ? <Dialog title="Review important email" onClose={() => { if (!busy) setOpen(false); }}>
      {preview === undefined ? <p>Loading recipients…</p> : !preview.allowed ? <p>Only the connected mailbox owner can send this notice.</p> : <EmailNoticeReview preview={preview} busy={busy} error={error} result={result} onApprove={() => { void send(); }} />}
    </Dialog> : null}
  </>;
}
